import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'components/@common/Inputs/Select';
import LicenseInformationCard from 'components/@common/LicenseInformationCard';
import { useFormikContext } from 'formik';
import { concat, filter, flow, sortBy, uniqBy } from 'lodash/fp';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import CancelIcon from '@material-ui/icons/Cancel';
import useProductCache from 'store/cache/product-cache';
import {
  OrganizationLicenseUsage,
  Product,
  ProductCurriculum,
  ProductType
} from 'types/schema-generated-types';
import {
  getDisplayName,
  getId,
  parseId,
  sortProductCurriculumByGrades
} from 'utilities/product-utils';
import { UserFormData } from '../types';

interface ProductItem {
  product: Product;
  siteLicensed: boolean;
  availableCount: number;
}

const useStyles = makeStyles(theme =>
  createStyles({
    inputLabel: {
      fontWeight: 600
    },
    licenseContainer: {
      display: 'grid',
      gridGap: 8,
      gridAutoRows: '1fr',
      gridAutoFlow: 'dense',
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2)
    },
    emptyContainer: {
      padding: theme.spacing(2)
    },
    productMenuItem: {
      fontWeight: 'bold',
      opacity: '1 !important'
    },
    curriculumMenuItem: {
      paddingLeft: theme.spacing(4)
    },
    buttonContainer: {
      marginTop: theme.spacing(1)
    }
  })
);

interface EducatorProductsPickerProps {
  licenseUsage: OrganizationLicenseUsage[];
}

const getEmptyProducts = (usages: OrganizationLicenseUsage[]) =>
  usages.reduce((acc, usage) => {
    if (usage.product.type !== ProductType.USER) return acc;
    if (acc.some(x => x.product.id === usage.product.id)) return acc;
    if (usages.some(x => x.product.id === usage.product.id && !x.productCurriculum?.id)) return acc;

    const product = { product: usage.product, siteLicensed: usage.siteLicensed, availableCount: 0 };
    return [...acc, product];
  }, [] as ProductItem[]);

const EducatorProductsPicker: React.FC<EducatorProductsPickerProps> = ({ licenseUsage }) => {
  const classes = useStyles();
  const [{ findProductByProductCode }] = useProductCache();
  const { handleChange, initialValues, setFieldValue, values } = useFormikContext<UserFormData>();
  const { t } = useTranslation('EducatorProductsPicker');

  const selectableProducts = useMemo(() => {
    // When a product has all its licenses assigned, the 'non-curriculum' license usage entry for
    // the product will no longer be included in the organization's license usage array. However,
    // we'll need these 'empty' products for this product picker to properly show tentative license
    // unassignments.
    const emptyProducts = getEmptyProducts(licenseUsage);

    const availableProducts: ProductItem[] = flow(
      filter((usage: OrganizationLicenseUsage) => {
        if (usage.product.type !== ProductType.USER) return false;
        if (usage.availableCount === 0 && !usage.siteLicensed) return false;
        if (usage.productCurriculum?.id) return false;
        return true;
      }),
      concat(emptyProducts),
      uniqBy('product.id'),
      sortBy('product.name')
    )(licenseUsage);

    return availableProducts.flatMap(({ product: productInput, siteLicensed, availableCount }) => {
      const product = findProductByProductCode(productInput.code);
      if (!product || !product.curriculums) return [];

      const productCurriculums = product.curriculums
        .filter(
          curriculum =>
            !values.licensedProducts.some(
              licensedProduct =>
                licensedProduct.product.id === product.id &&
                licensedProduct.productCurriculum?.id === curriculum.id
            )
        )
        .sort(sortProductCurriculumByGrades);

      if (!productCurriculums.length) return [];

      // Take into account tentatively-assigned products here - these are products/curriculums
      // that the admin has selected and "assigned" via the picker widget, but has not yet
      // clicked "Save & Close" to actually perform the assignment operation.

      // Count of currently-licensed products which are NOT in the initial list.
      const unsavedAssignCount = values.licensedProducts.filter(
        licensedProduct =>
          licensedProduct.product.id === product.id &&
          !initialValues.licensedProducts.some(
            i => i.productCurriculum?.id === licensedProduct.productCurriculum?.id
          )
      ).length;

      // Count of initially-licensed products which are NO LONGER in the current list.
      const unsavedUnassignCount = initialValues.licensedProducts.filter(
        licensedProduct =>
          licensedProduct.product.id === product.id &&
          !values.licensedProducts.some(
            i => i.productCurriculum?.id === licensedProduct.productCurriculum?.id
          )
      ).length;

      const tentativeAvailableCount = availableCount - unsavedAssignCount + unsavedUnassignCount;
      if (tentativeAvailableCount <= 0 && !siteLicensed) return [];

      const items: {
        product: Product;
        productCurriculum?: ProductCurriculum;
        siteLicensed?: boolean;
        availableCount?: number;
      }[] = [{ product, siteLicensed, availableCount: tentativeAvailableCount }];

      productCurriculums.forEach(productCurriculum => {
        items.push({ product, productCurriculum });
      });

      return items;
    });
  }, [findProductByProductCode, initialValues, licenseUsage, values]);

  const handleAddClick = useCallback(() => {
    if (!values.selectedProduct) return;
    const { productCode, productCurriculumCode } = parseId(values.selectedProduct);
    const selectedProduct = selectableProducts.find(
      ({ product, productCurriculum }) =>
        product.code === productCode && productCurriculum?.code === productCurriculumCode
    );

    if (selectedProduct) {
      setFieldValue('licensedProducts', [
        ...values.licensedProducts,
        { product: selectedProduct.product, productCurriculum: selectedProduct.productCurriculum }
      ]);
    }

    setFieldValue('selectedProduct', '');
  }, [selectableProducts, setFieldValue, values]);

  const handleRemoveClick = useCallback(
    licensedProductToRemove => (): void => {
      const newProductsValue = values.licensedProducts.filter(
        licensedProduct =>
          (licensedProduct.productCurriculum &&
            licensedProduct.productCurriculum.id !==
              licensedProductToRemove.productCurriculum.id) ||
          (!licensedProduct.productCurriculum &&
            licensedProduct.product.id !== licensedProductToRemove.product.id)
      );
      setFieldValue('licensedProducts', newProductsValue);
    },
    [setFieldValue, values]
  );

  return (
    <Grid container direction="column">
      <Grid>
        <Typography variant="body1" className={classes.inputLabel}>
          {t('Educator Products')}
        </Typography>
      </Grid>
      <Grid>
        {values.licensedProducts.length ? (
          <div className={classes.licenseContainer}>
            {values.licensedProducts.map((licensedProduct, index) => (
              <LicenseInformationCard
                key={`license_${getId(licensedProduct.product, licensedProduct.productCurriculum)}`}
                product={licensedProduct.product}
                productCurriculum={licensedProduct.productCurriculum}
                actionButton={
                  <Button
                    disableElevation
                    size="small"
                    color="secondary"
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleRemoveClick(licensedProduct)}
                  >
                    {t('Remove', { ns: '@common' })}
                  </Button>
                }
              />
            ))}
          </div>
        ) : (
          <div className={classes.emptyContainer}>
            <Typography>{t('No assigned products')}</Typography>
          </div>
        )}
      </Grid>
      <Grid>
        <Select
          disabled={!selectableProducts.length}
          helperText=""
          placeholder={t('Product Name')}
          size="small"
          name="selectedProduct"
          label={t('Assign Products')}
          value={values.selectedProduct}
          onChange={handleChange}
        >
          {selectableProducts.map(
            ({ product, productCurriculum, availableCount, siteLicensed }) => {
              if (!productCurriculum) {
                return (
                  <MenuItem
                    disabled
                    key={`assign_select_${getId(product)}`}
                    value={getId(product)}
                    className={classes.productMenuItem}
                  >
                    {getDisplayName(product)} {!siteLicensed && <>({availableCount} Available)</>}
                  </MenuItem>
                );
              }
              return (
                <MenuItem
                  key={`assign_select_${getId(product, productCurriculum)}`}
                  value={getId(product, productCurriculum)}
                  className={classes.curriculumMenuItem}
                >
                  {productCurriculum.name} ({productCurriculum.code})
                </MenuItem>
              );
            }
          )}
        </Select>
      </Grid>
      <Grid container justify="flex-end" className={classes.buttonContainer}>
        <Button
          size="small"
          color="secondary"
          variant="contained"
          onClick={handleAddClick}
          disableElevation
          disabled={!selectableProducts.length || !values.selectedProduct}
        >
          {t('Assign Product')}
        </Button>
      </Grid>
    </Grid>
  );
};

export default EducatorProductsPicker;
